import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Calendar, Video, Phone, Building2, Users, UserCog, AlertTriangle, Save, X, CheckCircle2, Download, Link as LinkIcon, MapPin, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/** Phase 157 — compact ICS preview shown on hover of the `.ics` button so
 *  the recruiter can confirm the time/mode/link before downloading.
 */
function IcsPreviewTooltip({ it, children }) {
    const date = it.scheduled_at ? new Date(it.scheduled_at) : null;
    const when = date ? date.toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
    }) : "Not scheduled yet";
    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>{children}</TooltipTrigger>
                <TooltipContent side="top" align="end" className="max-w-[320px] p-3 space-y-1.5">
                    <div className="font-display font-bold text-sm">{it.round_name || "Interview"}</div>
                    <div className="text-[11px] font-mono text-foreground/70 flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 shrink-0" />
                        <span>{when}{it.duration_minutes ? ` · ${it.duration_minutes}m` : ""}</span>
                    </div>
                    {it.meet_link ? (
                        <div className="text-[11px] font-mono text-ikb flex items-center gap-1.5 truncate" title={it.meet_link}>
                            <LinkIcon className="w-3 h-3 shrink-0" />
                            <span className="truncate">{it.meet_link}</span>
                        </div>
                    ) : it.location ? (
                        <div className="text-[11px] font-mono text-foreground/70 flex items-center gap-1.5 truncate" title={it.location}>
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{it.location}</span>
                        </div>
                    ) : (
                        <div className="text-[11px] font-mono text-foreground/50">No meet link / location set</div>
                    )}
                    {(it.interviewer_names || []).length > 0 && (
                        <div className="text-[11px] font-mono text-foreground/70 truncate">
                            Interviewers: {it.interviewer_names.join(", ")}
                        </div>
                    )}
                    <div className="text-[10px] font-mono text-foreground/50 pt-1 border-t border-border">
                        Candidate variant · click to download
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

const STATUS = ["Scheduled", "Completed", "Cancelled", "Rescheduled", "No-show"];
const OUTCOME = ["Pending", "Strong Hire", "Hire", "Lean Hire", "Lean No", "No Hire", "Pass", "Fail"];
const MODE = ["Phone", "Video", "Onsite", "Take-home"];

function ModeIcon({ mode }) {
    if (mode === "Phone") return <Phone className="w-3 h-3" />;
    if (mode === "Video") return <Video className="w-3 h-3" />;
    if (mode === "Onsite") return <Building2 className="w-3 h-3" />;
    return <Calendar className="w-3 h-3" />;
}

const blankForm = () => ({
    round_name: "Phone Screen",
    round_number: 1,
    scheduled_at: "",
    interviewer_ids: [],
    extra_names: "",            // ad-hoc comma-separated names (external interviewers)
    mode: "Video",
    duration_minutes: 60,
    application_id: "",
    status: "Scheduled",
    outcome: "Pending",
    feedback: "",
    score: "",
});

/* InterviewRow — one row in the Interviews tab.
   Uses LOCAL state so typing in feedback/score doesn't fire a PATCH on every
   keystroke (auto-save would clobber focus and cause reload jank). Saves
   on blur OR via the "Save" button if dirty. */
function InterviewRow({ it, isInterviewer, userId, onUpdate, onRemove, team = [] }) {
    const assignedToMe = isInterviewer && (it.interviewer_ids || []).includes(userId);
    const readOnly = isInterviewer && !assignedToMe;
    const closures = it.interviewer_closures || [];
    const closedByCount = closures.length;
    const totalAssigned = (it.interviewer_ids || []).length;
    const closuresLog = it.interviewer_closures_log || [];
    const interviewerClosedNames = closuresLog.map((c) => c.user_name).filter(Boolean);
    const fullyClosed = totalAssigned > 0 && closedByCount >= totalAssigned;
    const noInterviewer = totalAssigned === 0;

    const [status, setStatus] = useState(it.status || "Scheduled");
    const [outcome, setOutcome] = useState(it.outcome || "Pending");
    const [score, setScore] = useState(it.score ?? "");
    const [feedback, setFeedback] = useState(it.feedback || "");

    // ── Inline editor for interviewer assignment + scheduled time ──
    // Recruiters were forced to delete + recreate when they wanted to add
    // an interviewer or change the slot. This editor lets them tweak both
    // in place, with an explicit Save button so accidental clicks don't fire.
    const [editAssign, setEditAssign] = useState(false);
    const [editIds, setEditIds] = useState(it.interviewer_ids || []);
    const [editExtra, setEditExtra] = useState((it.interviewer_names || []).filter((n) => !(it.interviewer_ids || []).some((id) => (team.find((t) => t.id === id)?.name) === n)).join(", "));
    const [editScheduled, setEditScheduled] = useState(it.scheduled_at ? it.scheduled_at.slice(0, 16) : "");
    const [editDuration, setEditDuration] = useState(it.duration_minutes ?? 60);
    const [editMode, setEditMode] = useState(it.mode || "Video");
    const [savingAssign, setSavingAssign] = useState(false);
    // Phase 158f — one-click "Resend invite" from the candidate's interviews
    // panel. Reuses the already-wired /interviews/{id}/send-invite endpoint
    // which now does a real dispatch (candidate + team + ICS) instead of the
    // Phase-61 mock. Local `inviteSentAt` mirror lets us flash the success
    // pill without waiting for the parent list to refetch.
    const [resending, setResending] = useState(false);
    const [inviteSentAt, setInviteSentAt] = useState(it.invite_sent_at || null);
    const teamById = Object.fromEntries(team.map((t) => [t.id, t]));
    const idsToNames = (ids) => (ids || []).map((id) => teamById[id]?.name).filter(Boolean);

    // Re-sync from props when the row updates server-side (round reload).
    useEffect(() => {
        setStatus(it.status || "Scheduled");
        setOutcome(it.outcome || "Pending");
        setScore(it.score ?? "");
        setFeedback(it.feedback || "");
        setEditIds(it.interviewer_ids || []);
        setEditScheduled(it.scheduled_at ? it.scheduled_at.slice(0, 16) : "");
        setEditDuration(it.duration_minutes ?? 60);
        setEditMode(it.mode || "Video");
        setInviteSentAt(it.invite_sent_at || null);
    }, [it.id, it.status, it.outcome, it.score, it.feedback, it.scheduled_at, it.duration_minutes, it.mode, it.updated_at, it.invite_sent_at]);

    const resendInvite = async () => {
        if (resending) return;
        // Guard-rails so recruiters can't fire a resend that will inevitably
        // fail on the backend — surface the reason in the toast immediately.
        if (!it.candidate_id) {
            toast.error("Assign a candidate before sending the invite.");
            return;
        }
        if (!it.scheduled_at) {
            toast.error("Set a scheduled time before sending the invite.");
            return;
        }
        setResending(true);
        try {
            const { data } = await api.post(`/interviews/${it.id}/send-invite`, { note: "" });
            if (data?.ok === false) {
                // Dispatcher returned a soft failure (e.g. templates disabled);
                // show whatever hint it bubbled up but don't swallow the error.
                toast.warning(data?.report?.reason
                    ? `Resend reported: ${data.report.reason}`
                    : "Resend accepted but delivery was not confirmed.");
            } else {
                toast.success("Invite resent · calendar event will update");
            }
            setInviteSentAt(data?.sent_at || new Date().toISOString());
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setResending(false); }
    };

    const toggleEditId = (id) => {
        setEditIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
    };

    const saveAssignment = async () => {
        setSavingAssign(true);
        try {
            const extras = (editExtra || "").split(",").map((s) => s.trim()).filter(Boolean);
            const teamNames = idsToNames(editIds);
            await onUpdate(it.id, {
                interviewer_ids: editIds,
                interviewer_names: [...teamNames, ...extras],
                scheduled_at: editScheduled || null,
                duration_minutes: parseInt(editDuration) || null,
                mode: editMode,
            });
            setEditAssign(false);
        } finally { setSavingAssign(false); }
    };

    const pushIfChanged = (field, value, parsed) => {
        const cur = it[field];
        if ((parsed === null || parsed === undefined) && (cur === null || cur === undefined || cur === "")) return;
        if (parsed === cur) return;
        onUpdate(it.id, { [field]: parsed });
    };

    return (
        <div className="border border-border bg-white p-4 space-y-3" data-testid={`interview-row-${it.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-widest font-mono border border-border px-2 py-0.5">R{it.round_number}</span>
                        <span className="font-display font-bold">{it.round_name}</span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-0.5"><ModeIcon mode={it.mode} /> {it.mode}</span>
                        {assignedToMe && <span className="text-[10px] font-mono uppercase tracking-widest border border-ikb bg-blue-50 text-ikb px-2 py-0.5">Assigned to you</span>}
                        {it.status === "Completed" && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-emerald-400 bg-emerald-100 text-emerald-800 px-2 py-0.5 font-bold" data-testid={`interview-completed-${it.id}`}>
                                <CheckCircle2 className="w-3 h-3" /> Completed
                            </span>
                        )}
                        {totalAssigned > 0 && closedByCount > 0 && (
                            <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${fullyClosed ? "border-emerald-400 bg-emerald-100 text-emerald-800" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`} data-testid={`interview-closures-${it.id}`}>
                                {closedByCount}/{totalAssigned} interviewer{totalAssigned === 1 ? "" : "s"} closed
                            </span>
                        )}
                        {noInterviewer && !isInterviewer && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-amber-400 bg-amber-50 text-amber-800 px-2 py-0.5" data-testid={`interview-no-assignee-${it.id}`}>
                                <AlertTriangle className="w-3 h-3" /> No interviewer assigned
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-1">
                        {it.scheduled_at ? new Date(it.scheduled_at).toLocaleString() : "Not scheduled"}
                        {it.duration_minutes ? ` · ${it.duration_minutes}m` : ""}
                        {it.interviewer_names?.length ? ` · ${it.interviewer_names.join(", ")}` : ""}
                    </div>
                    {interviewerClosedNames.length > 0 && !isInterviewer && (
                        <div className="text-[11px] font-mono text-emerald-700 mt-1" data-testid={`interview-closed-by-${it.id}`}>
                            Closed by: {interviewerClosedNames.join(", ")}
                        </div>
                    )}
                </div>
                {!isInterviewer && (
                    <div className="flex items-center gap-1.5 shrink-0">
                        {it.scheduled_at && it.candidate_id && (
                            <>
                                <button
                                    type="button"
                                    onClick={resendInvite}
                                    disabled={resending}
                                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 border bg-ikb hover:bg-[#001f7a] text-white border-ikb disabled:opacity-60"
                                    data-testid={`resend-invite-${it.id}`}
                                    title={inviteSentAt
                                        ? `Last sent ${new Date(inviteSentAt).toLocaleString()} · click to resend with current time/mode`
                                        : "Send the calendar invite to the candidate + interviewers"}
                                >
                                    {resending
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <Send className="w-3 h-3" />}
                                    {inviteSentAt ? "Resend invite" : "Send invite"}
                                </button>
                                <IcsPreviewTooltip it={it}>
                                    <a
                                        href={`${API}/candidates/${it.candidate_id}/interviews/${it.id}/invite.ics`}
                                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 border bg-white text-foreground border-border hover:bg-secondary"
                                        data-testid={`download-ics-${it.id}`}
                                    >
                                        <Download className="w-3 h-3" /> .ics
                                    </a>
                                </IcsPreviewTooltip>
                            </>
                        )}
                        <button
                            onClick={() => setEditAssign((v) => !v)}
                            className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 border ${editAssign ? "bg-foreground text-background border-foreground" : "bg-white text-foreground border-border hover:bg-secondary"}`}
                            data-testid={`edit-assign-${it.id}`}
                            title="Edit interviewers and schedule"
                        >
                            <UserCog className="w-3 h-3" /> {editAssign ? "Cancel" : "Edit assignment"}
                        </button>
                        <button onClick={() => onRemove(it.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`remove-interview-${it.id}`} title="Delete this round">
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>
            {inviteSentAt && !isInterviewer && (
                <div className="text-[11px] font-mono text-emerald-700 flex items-center gap-1.5"
                     data-testid={`invite-sent-at-${it.id}`}>
                    <CheckCircle2 className="w-3 h-3" />
                    Invite sent · {new Date(inviteSentAt).toLocaleString()}
                </div>
            )}

            {/* Inline interviewer + schedule editor — only for recruiters/employers */}
            {!isInterviewer && editAssign && (
                <div className="border border-foreground bg-foreground/5 p-3 space-y-3" data-testid={`edit-assign-panel-${it.id}`}>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest font-bold">Scheduled at</Label>
                            <Input type="datetime-local" value={editScheduled} onChange={(e) => setEditScheduled(e.target.value)} className="rounded-none mt-1 h-9" data-testid={`edit-scheduled-${it.id}`} />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest font-bold">Duration (min)</Label>
                            <Input type="number" min={5} step={5} value={editDuration} onChange={(e) => setEditDuration(e.target.value)} className="rounded-none mt-1 h-9" data-testid={`edit-duration-${it.id}`} />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest font-bold">Mode</Label>
                            <select value={editMode} onChange={(e) => setEditMode(e.target.value)} className="mt-1 h-9 w-full px-2 border border-border bg-white text-sm" data-testid={`edit-mode-${it.id}`}>
                                {MODE.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1.5">
                            <Users className="w-3 h-3" /> Interviewers ({editIds.length} selected)
                        </Label>
                        {team.length === 0 ? (
                            <p className="text-[11px] text-muted-foreground font-mono mt-2">
                                No interviewers added yet. <a href="/company" className="text-ikb underline">Add interviewers on the Company page →</a>
                            </p>
                        ) : (
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`edit-picker-${it.id}`}>
                                {team.map((t) => {
                                    const on = editIds.includes(t.id);
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => toggleEditId(t.id)}
                                            className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${on ? "bg-ikb text-white border-ikb" : "bg-white border-border hover:bg-secondary"}`}
                                            data-testid={`edit-pick-${it.id}-${t.id}`}
                                        >
                                            {on ? "✓ " : ""}{t.name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <Input value={editExtra} onChange={(e) => setEditExtra(e.target.value)} placeholder="External interviewers (comma-separated names)" className="rounded-none mt-2 h-9" data-testid={`edit-extra-names-${it.id}`} />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                        <Button onClick={saveAssignment} disabled={savingAssign} className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`edit-assign-save-${it.id}`}>
                            <Save className="w-3.5 h-3.5 mr-1" /> {savingAssign ? "Saving…" : "Save assignment"}
                        </Button>
                        <button onClick={() => setEditAssign(false)} className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 hover:bg-secondary" data-testid={`edit-assign-cancel-${it.id}`}>
                            <X className="w-3.5 h-3.5 inline -mt-0.5" /> Cancel
                        </button>
                    </div>
                </div>
            )}
            <div className="grid md:grid-cols-3 gap-3">
                <select
                    disabled={readOnly}
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); onUpdate(it.id, { status: e.target.value }); }}
                    className="h-9 px-2 border border-border bg-white text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`interview-status-${it.id}`}
                >
                    {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    disabled={readOnly}
                    value={outcome}
                    onChange={(e) => { setOutcome(e.target.value); onUpdate(it.id, { outcome: e.target.value }); }}
                    className="h-9 px-2 border border-border bg-white text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`interview-outcome-${it.id}`}
                >
                    {OUTCOME.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <Input
                    disabled={readOnly}
                    type="number"
                    min={1}
                    max={10}
                    placeholder="Score 1–10"
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                    onBlur={() => pushIfChanged("score", score, score === "" ? null : parseInt(score))}
                    className="rounded-none h-9 disabled:opacity-60"
                    data-testid={`interview-score-${it.id}`}
                />
            </div>
            <div>
                <Textarea
                    disabled={readOnly}
                    rows={3}
                    placeholder={readOnly ? "Only assigned interviewers can edit feedback" : "Feedback..."}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    onBlur={() => pushIfChanged("feedback", feedback, feedback || "")}
                    className="rounded-none text-sm disabled:opacity-60"
                    data-testid={`interview-feedback-${it.id}`}
                />
                {it.feedback_author_name && (
                    <div className="text-[10px] font-mono text-muted-foreground mt-1" data-testid={`interview-feedback-author-${it.id}`}>
                        Last updated by {it.feedback_author_name}{it.updated_at ? ` · ${new Date(it.updated_at).toLocaleString()}` : ""}
                    </div>
                )}
            </div>
            {/* Per-interviewer assessments — snapshot of feedback + rating + Q&A
                each interviewer left when they closed their round. Only shown
                to recruiter/employer (not the interviewer themselves) and only
                when at least one interviewer has saved feedback. */}
            {!isInterviewer && (it.interviewer_feedbacks || []).length > 0 && (
                <InterviewerAssessments feedbacks={it.interviewer_feedbacks} iid={it.id} />
            )}
        </div>
    );
}

/* Collapsible per-interviewer assessment list.
   Each card shows: interviewer name, status badge, outcome, score, full
   feedback text, rating snapshot (hard/soft/critical with stars), and
   Q&A snapshot (Q + answer + per-question score).
   The whole panel collapses by default and expands on click. */
function InterviewerAssessments({ feedbacks, iid }) {
    const [openIds, setOpenIds] = useState(() => new Set([feedbacks[0]?.user_id].filter(Boolean)));
    const toggle = (uid) => setOpenIds((s) => {
        const n = new Set(s);
        if (n.has(uid)) n.delete(uid); else n.add(uid);
        return n;
    });
    return (
        <div className="border border-foreground/10 bg-secondary/20" data-testid={`assessments-${iid}`}>
            <div className="px-3 py-2 bg-foreground text-background flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-display font-bold text-sm">Interviewer assessments</span>
                <span className="text-[10px] font-mono uppercase tracking-widest opacity-70 ml-auto">{feedbacks.length} submitted</span>
            </div>
            <div className="divide-y divide-border">
                {feedbacks.map((fb) => {
                    const open = openIds.has(fb.user_id);
                    const recColor = (fb.outcome || "").toLowerCase().includes("hire")
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                        : (fb.outcome || "").toLowerCase().includes("no")
                            ? "border-rose-300 bg-rose-50 text-rose-800"
                            : "border-border bg-white text-foreground/70";
                    const rating = fb.rating_snapshot || {};
                    const qa = fb.qa_snapshot || [];
                    return (
                        <div key={fb.user_id} className="bg-white" data-testid={`assessment-${iid}-${fb.user_id}`}>
                            <button
                                type="button"
                                onClick={() => toggle(fb.user_id)}
                                className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-secondary/40"
                                data-testid={`assessment-toggle-${iid}-${fb.user_id}`}
                            >
                                <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold shrink-0">
                                    {(fb.user_name || "?").slice(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">{fb.user_name || "Unknown"}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">
                                        {fb.closed_at ? `Closed ${new Date(fb.closed_at).toLocaleString()}` : (fb.updated_at ? `Saved ${new Date(fb.updated_at).toLocaleString()}` : "In progress")}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {fb.score != null && <span className="font-mono text-sm font-bold tabular-nums">{fb.score}/10</span>}
                                    {fb.outcome && <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${recColor}`}>{fb.outcome}</span>}
                                    {fb.closed_at && <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-400 bg-emerald-100 text-emerald-800 px-1.5 py-0.5">Closed</span>}
                                    <span className="text-foreground/40 text-xs">{open ? "−" : "+"}</span>
                                </div>
                            </button>
                            {open && (
                                <div className="px-3 pb-3 pt-1 space-y-3 border-t border-border bg-secondary/20" data-testid={`assessment-detail-${iid}-${fb.user_id}`}>
                                    {fb.feedback ? (
                                        <div>
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">Feedback</div>
                                            <p className="text-xs leading-relaxed whitespace-pre-wrap bg-white border border-border p-2.5">{fb.feedback}</p>
                                        </div>
                                    ) : (
                                        <div className="text-[11px] text-foreground/40">No written feedback.</div>
                                    )}
                                    {rating.summary && (
                                        <div>
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">Rating summary</div>
                                            <p className="text-xs leading-relaxed whitespace-pre-wrap">{rating.summary}</p>
                                        </div>
                                    )}
                                    {(rating.overall != null || rating.recommendation) && (
                                        <div className="flex items-center gap-3 text-xs">
                                            {rating.overall != null && <span className="font-mono">Overall: <span className="font-bold">{rating.overall}/10</span></span>}
                                            {rating.recommendation && <span className="font-mono">Rec: <span className="font-bold">{rating.recommendation}</span></span>}
                                        </div>
                                    )}
                                    <AssessmentSkillGroup label="Hard skills" obj={rating.hard_skills} />
                                    <AssessmentSkillGroup label="Soft skills" obj={rating.soft_skills} />
                                    <AssessmentSkillGroup label="Critical parameters" obj={rating.critical} />
                                    {qa.length > 0 && (
                                        <div>
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">Q&amp;A snapshot ({qa.length})</div>
                                            <div className="space-y-2">
                                                {qa.map((q, i) => (
                                                    <div key={i} className="border border-border bg-white p-2.5" data-testid={`assessment-qa-${iid}-${fb.user_id}-${i}`}>
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 shrink-0 mt-0.5">Q{(q.order ?? i) + 1}</span>
                                                            <p className="text-xs font-semibold flex-1">{q.question}</p>
                                                            {q.score != null && <span className="text-[10px] font-mono font-bold border border-border px-1.5 py-0.5">{q.score}/10</span>}
                                                        </div>
                                                        {q.answer ? (
                                                            <p className="text-xs text-foreground/80 mt-1.5 pl-6 whitespace-pre-wrap">{q.answer}</p>
                                                        ) : (
                                                            <p className="text-[11px] text-foreground/40 mt-1.5 pl-6 italic">(no answer recorded)</p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AssessmentSkillGroup({ label, obj }) {
    const entries = Object.entries(obj || {}).filter(([, v]) => v);
    if (entries.length === 0) return null;
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/50 mb-1">{label}</div>
            <div className="grid grid-cols-2 gap-y-1 gap-x-3">
                {entries.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs">
                        <span className="truncate">{k}</span>
                        <span className="font-mono tabular-nums text-foreground/70">{v}/5</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function CandidateInterviews({ candidateId }) {
    const { user } = useAuth();
    const isInterviewer = user?.role === "interviewer";
    const [items, setItems] = useState([]);
    const [team, setTeam] = useState([]);          // company interviewers (id + name)
    const [apps, setApps] = useState([]);          // candidate applications for linking
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(blankForm);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidateId}/interviews`);
            setItems(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const loadCtx = async () => {
        try {
            if (!isInterviewer) {
                const [t, c] = await Promise.all([
                    api.get("/company/interviewers").catch(() => ({ data: [] })),
                    api.get(`/candidates/${candidateId}`).catch(() => ({ data: { applications: [] } })),
                ]);
                setTeam(t.data || []);
                setApps(c.data?.applications || []);
            }
        } catch { /* ignore */ }
    };

    useEffect(() => { load(); loadCtx(); /* eslint-disable-next-line */ }, [candidateId]);

    const teamById = Object.fromEntries(team.map((t) => [t.id, t]));
    const idsToNames = (ids) => (ids || []).map((id) => teamById[id]?.name).filter(Boolean);

    const toggleInterviewerId = (id) => {
        setForm((f) => {
            const has = f.interviewer_ids.includes(id);
            return { ...f, interviewer_ids: has ? f.interviewer_ids.filter((x) => x !== id) : [...f.interviewer_ids, id] };
        });
    };

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const extras = (form.extra_names || "").split(",").map((s) => s.trim()).filter(Boolean);
            const teamNames = idsToNames(form.interviewer_ids);
            const app = apps.find((a) => a.id === form.application_id);
            await api.post(`/candidates/${candidateId}/interviews`, {
                round_name: form.round_name,
                round_number: parseInt(form.round_number) || 1,
                scheduled_at: form.scheduled_at || null,
                interviewer_ids: form.interviewer_ids,
                interviewer_names: [...teamNames, ...extras],
                mode: form.mode,
                duration_minutes: parseInt(form.duration_minutes) || null,
                application_id: form.application_id || null,
                job_id: app?.job_id || null,
                status: form.status,
                outcome: form.outcome,
            });
            toast.success("Interview scheduled");
            setOpen(false);
            setForm(blankForm());
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const update = async (id, patch) => {
        try {
            await api.patch(`/candidates/${candidateId}/interviews/${id}`, patch);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this interview?")) return;
        try {
            await api.delete(`/candidates/${candidateId}/interviews/${id}`);
            toast.success("Deleted");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-display font-bold text-xl">Interview rounds</h3>
                {!isInterviewer && (
                    <Button onClick={() => setOpen((o) => !o)} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-9" data-testid="add-interview-btn">
                        <Plus className="w-4 h-4 mr-1" /> {open ? "Close" : "Add round"}
                    </Button>
                )}
            </div>

            {!isInterviewer && open && (
                <form onSubmit={create} className="border border-border bg-white p-5 grid md:grid-cols-3 gap-3">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Round name</Label>
                        <Input required value={form.round_name} onChange={(e) => setForm({ ...form, round_name: e.target.value })} className="rounded-none mt-2 h-10" data-testid="interview-round-name" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Round #</Label>
                        <Input type="number" min={1} value={form.round_number} onChange={(e) => setForm({ ...form, round_number: e.target.value })} className="rounded-none mt-2 h-10" data-testid="interview-round-num" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Mode</Label>
                        <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="mt-2 h-10 w-full px-3 border border-border bg-white text-sm" data-testid="interview-mode">
                            {MODE.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Scheduled at</Label>
                        <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="rounded-none mt-2 h-10" data-testid="interview-scheduled" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Duration (min)</Label>
                        <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="rounded-none mt-2 h-10" data-testid="interview-duration" />
                    </div>
                    {apps.length > 0 && (
                        <div>
                            <Label className="text-xs uppercase tracking-widest font-bold">For job</Label>
                            <select value={form.application_id} onChange={(e) => setForm({ ...form, application_id: e.target.value })} className="mt-2 h-10 w-full px-3 border border-border bg-white text-sm" data-testid="interview-application">
                                <option value="">— optional —</option>
                                {apps.map((a) => <option key={a.id} value={a.id}>{a.job_title || a.job_id}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="md:col-span-3">
                        <Label className="text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" /> Interviewers
                        </Label>
                        {team.length === 0 ? (
                            <p className="text-[11px] text-muted-foreground font-mono mt-2">
                                No interviewers added yet.{" "}
                                <a href="/company" className="text-ikb underline">Add interviewers on the Company page →</a>
                            </p>
                        ) : (
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="interview-interviewer-picker">
                                {team.map((t) => {
                                    const on = form.interviewer_ids.includes(t.id);
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => toggleInterviewerId(t.id)}
                                            className={`text-[11px] font-mono uppercase tracking-widest px-2 py-1 border ${on ? "bg-ikb text-white border-ikb" : "bg-white border-border hover:bg-secondary"}`}
                                            data-testid={`interview-pick-${t.id}`}
                                        >
                                            {on ? "✓ " : ""}{t.name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <Input value={form.extra_names} onChange={(e) => setForm({ ...form, extra_names: e.target.value })} placeholder="External interviewers (comma-separated names)" className="rounded-none mt-2 h-10" data-testid="interview-extra-names" />
                    </div>
                    <div className="md:col-span-3">
                        <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="interview-create">{busy ? "Scheduling..." : "Schedule"}</Button>
                    </div>
                </form>
            )}

            {items.length === 0 ? (
                <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No interviews scheduled yet.</div>
            ) : (
                <div className="space-y-3">
                    {items.map((it) => (
                        <InterviewRow
                            key={it.id}
                            it={it}
                            isInterviewer={isInterviewer}
                            userId={user?.id}
                            onUpdate={update}
                            onRemove={remove}
                            team={team}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
